/**
 * StudentContext
 *
 * Manages the list of children belonging to the authenticated parent,
 * the active child (who is "playing" right now), and whether the parent
 * PIN session has been verified this browser session.
 *
 * sessionStorage keys:
 *   tinkerbots_active_child  — childId of the currently playing child
 *   tinkerbots_parent_pin_ok — "1" if parent PIN was verified this session
 *   tinkerbots_child_pin_ok  — childId if that child's PIN was verified
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { Student, AgeGroup, Parent } from '@kids-ai/shared-types';
import { useAuth } from './AuthContext';

const KEY_ACTIVE_CHILD  = 'tinkerbots_active_child';
const KEY_PARENT_PIN_OK = 'tinkerbots_parent_pin_ok';
const KEY_CHILD_PIN_OK  = 'tinkerbots_child_pin_ok';

interface StudentContextValue {
  /** All children belonging to the authenticated parent */
  children:        Student[];
  /** The child who is currently "playing" */
  activeChild:     Student | null;
  /** The parent profile (includes pinHash) */
  parentProfile:   Parent | null;
  isLoading:       boolean;

  /** Whether the parent entered their PIN this session */
  parentPinVerified: boolean;
  setParentPinVerified: (v: boolean) => void;

  /** childId whose PIN was verified this session (clears when switching) */
  childPinVerifiedId: string | null;
  setChildPinVerifiedId: (id: string | null) => void;

  setActiveChild:    (child: Student | null) => void;
  refreshChildren:   () => Promise<void>;
  refreshParent:     () => Promise<void>;
  addXP:             (amount: number) => void;
  ageGroup:          AgeGroup | null;

  // Legacy aliases
  student:    Student | null;
  setStudent: (s: Student | null) => void;
}

const StudentContext = createContext<StudentContextValue | null>(null);

export function StudentProvider({ children: reactChildren }: { children: ReactNode }) {
  const { isAuthenticated, getIdToken } = useAuth();

  const [childList,     setChildList]     = useState<Student[]>([]);
  const [activeChild,   setActiveChildSt] = useState<Student | null>(null);
  const [parentProfile, setParentProfile] = useState<Parent | null>(null);
  const [isLoading,     setIsLoading]     = useState(false);

  // PIN session flags (sessionStorage so they clear on browser close)
  const [parentPinVerified,  setParentPinVerifiedSt]  = useState(
    () => sessionStorage.getItem(KEY_PARENT_PIN_OK) === '1'
  );
  const [childPinVerifiedId, setChildPinVerifiedIdSt] = useState<string | null>(
    () => sessionStorage.getItem(KEY_CHILD_PIN_OK)
  );

  // ── Load parent profile ────────────────────────────────────────────────────
  const refreshParent = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getIdToken();
      if (!token) return;
      const res  = await fetch('/api/v1/parents/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error('[StudentContext] GET /parents/me failed', res.status);
        return;
      }
      const json = await res.json() as { success: boolean; data: Parent };
      if (json.success) setParentProfile(json.data);
    } catch (err) {
      console.warn('[StudentContext] refreshParent failed', err);
    }
  }, [isAuthenticated, getIdToken]);

  // ── Load children ──────────────────────────────────────────────────────────
  const refreshChildren = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res  = await fetch('/api/v1/children', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[StudentContext] GET /children failed', res.status, text);
        return;
      }
      const json = await res.json() as { success: boolean; data: Student[] };
      if (!json.success) {
        console.error('[StudentContext] GET /children returned success:false', json);
        return;
      }
      if (json.success) {
        setChildList(json.data);
        // Re-hydrate active child from sessionStorage
        const storedId = sessionStorage.getItem(KEY_ACTIVE_CHILD);
        if (storedId) {
          const found = json.data.find(c => c.id === storedId);
          if (found) setActiveChildSt(found);
        }
      }
    } catch (err) {
      console.warn('[StudentContext] refreshChildren failed', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getIdToken]);

  useEffect(() => {
    if (isAuthenticated) {
      void refreshParent();
      void refreshChildren();
    } else {
      setChildList([]);
      setActiveChildSt(null);
      setParentProfile(null);
      sessionStorage.removeItem(KEY_ACTIVE_CHILD);
      sessionStorage.removeItem(KEY_PARENT_PIN_OK);
      sessionStorage.removeItem(KEY_CHILD_PIN_OK);
    }
  }, [isAuthenticated, refreshParent, refreshChildren]);

  // ── setActiveChild ─────────────────────────────────────────────────────────
  const setActiveChild = useCallback((child: Student | null) => {
    setActiveChildSt(child);
    if (child) {
      sessionStorage.setItem(KEY_ACTIVE_CHILD, child.id);
    } else {
      sessionStorage.removeItem(KEY_ACTIVE_CHILD);
      // Clear child PIN verification when deselecting
      setChildPinVerifiedIdSt(null);
      sessionStorage.removeItem(KEY_CHILD_PIN_OK);
    }
  }, []);

  // ── PIN session setters ────────────────────────────────────────────────────
  const setParentPinVerified = useCallback((v: boolean) => {
    setParentPinVerifiedSt(v);
    if (v) sessionStorage.setItem(KEY_PARENT_PIN_OK, '1');
    else   sessionStorage.removeItem(KEY_PARENT_PIN_OK);
  }, []);

  const setChildPinVerifiedId = useCallback((id: string | null) => {
    setChildPinVerifiedIdSt(id);
    if (id) sessionStorage.setItem(KEY_CHILD_PIN_OK, id);
    else    sessionStorage.removeItem(KEY_CHILD_PIN_OK);
  }, []);

  // ── Optimistic XP ─────────────────────────────────────────────────────────
  const addXP = useCallback((amount: number) => {
    setActiveChildSt(prev => {
      if (!prev) return prev;
      const updated = { ...prev, xp: prev.xp + amount, totalXp: prev.totalXp + amount };
      setChildList(list => list.map(c => c.id === prev.id ? updated : c));
      return updated;
    });
  }, []);

  const value = useMemo<StudentContextValue>(() => ({
    children:             childList,
    activeChild,
    parentProfile,
    isLoading,
    parentPinVerified,
    setParentPinVerified,
    childPinVerifiedId,
    setChildPinVerifiedId,
    setActiveChild,
    refreshChildren,
    refreshParent,
    addXP,
    ageGroup:             activeChild?.ageGroup ?? null,
    student:              activeChild,
    setStudent:           setActiveChild,
  }), [
    childList, activeChild, parentProfile, isLoading,
    parentPinVerified, setParentPinVerified,
    childPinVerifiedId, setChildPinVerifiedId,
    setActiveChild, refreshChildren, refreshParent, addXP,
  ]);

  return (
    <StudentContext.Provider value={value}>
      {reactChildren}
    </StudentContext.Provider>
  );
}

export function useStudent() {
  const ctx = useContext(StudentContext);
  if (!ctx) throw new Error('useStudent must be used within StudentProvider');
  return ctx;
}
